/**
 * UserSettingsPage
 * Flutter source: lib/presentation/screens/settings/user_settings_screen.dart
 *
 * Displays the server-driven settings categories (Privacy, Content, Payment,
 * Notifications). Each row navigates to `SettingsCategoryPage`. Theme and
 * language have been moved to the top bar.
 */

import { useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';
import { FlexibleHeaderList } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderList';
import { FlexibleHeaderBackground } from '@/presentation/components/common/appBar/flexibleHeaderList/FlexibleHeaderBackground';
import type { SettingsListItem } from '@/domain/entities/userSettings/settingsCategoryInfo';
import { useSettingsListItems } from '../hooks/useSettingsListItems';
import { ListItemTile } from '@/presentation/components/common/ListItemTile';

export const UserSettingsPage = () => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();
  const location = useLocation();

  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname)
  );
  const screenKey = screenKeyRef.current;

  const listItems = useSettingsListItems();

  useBasePage({
    screenKey,
    onInit: () => { },
  });

  const handleItemClick = useCallback(
    (item: SettingsListItem) => {
      Navigator.navigateToSettingCategory(item.category);
    },
    []
  );

  const renderCategoryItem = useCallback(
    (item: SettingsListItem) => (
      <div style={{ padding: `0 ${responsive.spacing.base}px` }}>
        <ListItemTile
          title={item.title}
          description={item.description}
          iconPath={item.iconPath}
          onClick={() => handleItemClick(item)}
          showChevron
          qa={{ id: `settings-row-${item.category}` }}
        />
      </div>
    ),
    [responsive.spacing.base, handleItemClick]
  );

  const getItemKey = useCallback(
    (item: SettingsListItem) => `server-${item.category}`,
    []
  );

  return (
    <FlexibleHeaderList
      qa={{ id: 'user-settings', status: 'ready' }}
      listQa={{ id: 'user-settings-list' }}
      items={listItems}
      renderItem={renderCategoryItem}
      getItemKey={getItemKey}
      hasMore={false}
      onLoadMore={() => { }}
      backgroundColor={theme.background}
      appBarOptions={{
        title: l10n.settings.title,
        onLeadingTap: () => Navigator.goBack(),
        backgroundColor: theme.background,
        flexibleSpace: {
          parallax: true,
          background: (
            <FlexibleHeaderBackground
              // iconPath="/assets/images/settings.png"
              title={l10n.settings.title}
            />
          ),
        },
      }}
    />
  );
};
